import PropTypes from "prop-types";
import Typography from "../../../atoms/typography";
import LocationLock from "..";
import CustomImage from "../../../atoms/customImage";
import { LOCATION_LOCK_HEADING, LOCATION_LOCK_SUB_HEADING } from "../../../../constants/common/locationFieldConstants";
import { shallowEqual, useSelector } from "react-redux";
import { constants } from "../../../../constants/pageConstants/pdpConstants";

/**
 * LocationLockFields component
 *
 * This component renders a full-screen modal for mobile devices to display and manage the location lock functionality.
 * It provides a header with a back button and includes location-related content.
 *
 * @component
 * @returns {JSX.Element} The rendered mobile location lock component.
 */
const LocationLockFields = ({
  isFromOutsideDrawer = false,
  isFromPDP,
  isFromCheckout,
  fullProductDetails,
  isMandatoryLocation,
  isAutoForMobile,
  messageText,
  parentClass = "",
  setMandatoryError,
  isInternationalMobilePDP,
  isContinueButtonHidden = false,
  onClearSavedAddressSelection,
  isProductUnavailable = false,
  onExploreAvailableProducts,
  onSuggestionsVisibilityChange,
  isFromGiftFinderGate = false,
  locationCaptureMode,
  capturedLocation,
}) => {
  const { configData, isMobile } = useSelector(
    (state) => ({
      configData: state?.locationLock?.configData,
      isMobile: state?.appConfig?.isMobile,
    }),
    shallowEqual,
  );

  const locationLockProps = {
    isFromCheckout,
    fullProductDetails,
    isLocationError: isMandatoryLocation || isProductUnavailable,
    messageText: isProductUnavailable ? constants.PRODUCT_CANNOT_DELIVERED : messageText,
    isDisable: isAutoForMobile,
    setMandatoryError,
    isInternationalMobilePDP,
    isFromPDP,
    isSeparateFieldsDesign: isMobile || isFromCheckout,
    isContinueButtonHidden,
    onClearSavedAddressSelection,
    isProductUnavailable,
    onExploreAvailableProducts,
    onSuggestionsVisibilityChange,
    isFromOutsideDrawer,
    isFromGiftFinderGate,
    locationCaptureMode,
    capturedLocation,
  };

  return (
    <div id="locationLockWrapper">
      {isMobile || isFromCheckout ? (
        <div className="relative flex w-full flex-col" id="locationLock" data-testid="locationLock">
          <LocationLock {...locationLockProps} />
        </div>
      ) : (
        <div
          className={`border-gray-30 relative flex h-full w-full flex-col flex-wrap rounded-[16px] border border-thinLightGrey bg-white-900 px-16 py-20 md:h-max md:max-w-[440px] md:rounded-2xl ${parentClass}`}
          id="locationLock"
          data-testid="locationLock"
        >
          <div className="absolute right-2 top-2 hidden md:block" />
          <div className="flex w-full">
            <div className="pr-8">
              <CustomImage
                src="/icons/location-Icon.svg"
                alt=""
                sizes="10vw"
                width={24}
                height={24}
                isPriority={false}
              />
            </div>
            <Typography variant="p" Tag="p" classes="font-600 text-16 text-fnp-500">
              {configData?.title || LOCATION_LOCK_HEADING}
            </Typography>
          </div>
          <div className="w-full py-8">
            <Typography variant="p" Tag="p" classes="text-12 text-grey-500">
              {configData?.description || LOCATION_LOCK_SUB_HEADING}
            </Typography>
          </div>
          <LocationLock
            fullProductDetails={fullProductDetails}
            isLocationError={isMandatoryLocation || isProductUnavailable}
            messageText={isProductUnavailable ? constants.PRODUCT_CANNOT_DELIVERED : messageText}
            isDisable={isAutoForMobile}
            setMandatoryError={setMandatoryError}
            isInternationalMobilePDP={isInternationalMobilePDP}
            isFromPDP={isFromPDP}
            isProductUnavailable={isProductUnavailable}
            onExploreAvailableProducts={onExploreAvailableProducts}
            onSuggestionsVisibilityChange={onSuggestionsVisibilityChange}
            // Listed one by one rather than spreading locationLockProps: this branch
            // deliberately omits the layout props above, and the gift finder gate is the
            // only caller that reaches it on desktop — without these, confirming there
            // never records the answer and the quiz card renders the page-style chrome.
            isFromGiftFinderGate={isFromGiftFinderGate}
            locationCaptureMode={locationCaptureMode}
            capturedLocation={capturedLocation}
          />
        </div>
      )}
    </div>
  );
};
LocationLockFields.propTypes = {
  isFromOutsideDrawer: PropTypes.bool,
  isFromPDP: PropTypes.bool,
  isFromCheckout: PropTypes.bool,
  fullProductDetails: PropTypes.object,
  isMandatoryLocation: PropTypes.bool,
  isAutoForMobile: PropTypes.bool,
  messageText: PropTypes.string,
  parentClass: PropTypes.string,
  setMandatoryError: PropTypes.func,
  isInternationalMobilePDP: PropTypes.bool,
  isContinueButtonHidden: PropTypes.bool,
  onClearSavedAddressSelection: PropTypes.func,
  isProductUnavailable: PropTypes.bool,
  onExploreAvailableProducts: PropTypes.func,
  onSuggestionsVisibilityChange: PropTypes.func,
  isFromGiftFinderGate: PropTypes.bool,
  locationCaptureMode: PropTypes.shape({ onCapture: PropTypes.func.isRequired }),
  capturedLocation: PropTypes.shape({
    pincode: PropTypes.string,
    address: PropTypes.string,
    geoId: PropTypes.string,
  }),
};

export default LocationLockFields;
